import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, ExternalLink, Loader2, FileText, Image as ImageIcon, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import * as XLSX from "xlsx";

/** Popup view đầy đủ các loại file: image / pdf / excel / khác (download). */
export default function FileViewerDialog({ attachment, onClose }) {
  const [sheetData, setSheetData] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetErr, setSheetErr] = useState("");

  const mime = attachment?.mime_type || "";
  const fileName = attachment?.file_name || "";
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? (localStorage.getItem("karinox_token") || "") : "";
  // Proxy URL qua backend (có token trong query để <img>/<iframe> auth được) — tránh Discord CDN block browser
  const proxyUrl = attachment?.id ? `${backendUrl}/api/discord/attachments/${attachment.id}/download-proxy?t=${encodeURIComponent(token)}` : "";
  const discordUrl = attachment?.discord_url || "";

  const isImage = mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext);
  const isPdf = mime === "application/pdf" || ext === "pdf";
  const isExcel =
    mime.includes("spreadsheet") ||
    mime === "text/csv" ||
    ["xlsx", "xls", "csv"].includes(ext);
  const isVideo = mime.startsWith("video/") || ["mp4", "mov", "webm", "avi", "mkv"].includes(ext);
  const isAudio = mime.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a", "flac"].includes(ext);

  const loadSheet = useCallback(async () => {
    if (!attachment?.id) return;
    setSheetLoading(true);
    setSheetErr("");
    try {
      const token = localStorage.getItem("karinox_token") || "";
      const resp = await fetch(proxyUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const buf = await resp.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const out = {};
      wb.SheetNames.forEach((name) => {
        const ws = wb.Sheets[name];
        const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        out[name] = json.slice(0, 200); // giới hạn 200 rows để tránh đơ
      });
      setSheetData(out);
    } catch (e) {
      setSheetErr(e.message || "Lỗi đọc file");
    } finally {
      setSheetLoading(false);
    }
  }, [attachment, proxyUrl]);

  useEffect(() => {
    if (attachment && isExcel) loadSheet();
    else setSheetData(null);
  }, [attachment, isExcel, loadSheet]);

  if (!attachment) return null;

  const firstSheet = sheetData && Object.keys(sheetData)[0];
  const firstRows = firstSheet ? sheetData[firstSheet] : [];

  return (
    <Dialog open={!!attachment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="file-viewer-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
            {isImage && <ImageIcon className="w-4 h-4 text-violet-400" />}
            {(isPdf || isExcel || (!isImage && !isVideo && !isAudio)) && <FileText className="w-4 h-4 text-sky-400" />}
            <span className="truncate">{fileName}</span>
            <Badge className="bg-slate-700 text-slate-300 text-[9px] ml-auto">{mime || "unknown"}</Badge>
          </DialogTitle>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span>{(attachment.file_size / 1024).toFixed(1)} KB</span>
            <span>·</span>
            <span>#{attachment.channel_name}</span>
            <span>·</span>
            <span>{attachment.author_name}</span>
            {attachment.source === "forwarded" && (
              <Badge className="bg-violet-500/15 text-violet-400 border border-violet-500/30 text-[9px]">Chuyển tiếp</Badge>
            )}
          </div>
        </DialogHeader>

        <div className="mt-3">
          {isImage && (
            <div className="flex justify-center bg-[#0f172a] rounded-lg p-2">
              <img
                src={proxyUrl}
                alt={fileName}
                className="max-w-full max-h-[70vh] rounded"
                data-testid="file-viewer-image"
              />
            </div>
          )}

          {isPdf && (
            <iframe
              src={proxyUrl}
              className="w-full h-[70vh] bg-white rounded-lg border border-[#334155]"
              title={fileName}
              data-testid="file-viewer-pdf"
            />
          )}

          {isExcel && (
            <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-2 max-h-[70vh] overflow-auto" data-testid="file-viewer-sheet">
              {sheetLoading && (
                <div className="flex items-center justify-center py-12 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Đang đọc file...
                </div>
              )}
              {sheetErr && (
                <div className="flex items-center gap-2 text-red-400 p-4">
                  <AlertCircle className="w-4 h-4" /> {sheetErr}
                </div>
              )}
              {sheetData && firstRows.length > 0 && (
                <>
                  {Object.keys(sheetData).length > 1 && (
                    <div className="text-[11px] text-slate-500 mb-2">Sheet: {firstSheet}</div>
                  )}
                  <table className="w-full text-[11px] border-collapse">
                    <tbody>
                      {firstRows.map((row, ri) => (
                        <tr key={ri} className={ri === 0 ? "bg-[#334155]/50 font-bold" : "even:bg-[#0f172a]"}>
                          {row.map((cell, ci) => (
                            <td key={ci} className="border border-[#334155] px-2 py-1 text-slate-300">
                              {String(cell)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {firstRows.length === 200 && (
                    <p className="text-[10px] text-slate-600 mt-2">Hiển thị 200 dòng đầu. Tải về để xem đầy đủ.</p>
                  )}
                </>
              )}
            </div>
          )}

          {isVideo && (
            <video src={proxyUrl} controls className="w-full max-h-[70vh] rounded-lg" data-testid="file-viewer-video" />
          )}
          {isAudio && (
            <audio src={proxyUrl} controls className="w-full" data-testid="file-viewer-audio" />
          )}

          {!isImage && !isPdf && !isExcel && !isVideo && !isAudio && (
            <div className="flex flex-col items-center justify-center py-16 bg-[#0f172a] rounded-lg" data-testid="file-viewer-other">
              <FileText className="w-16 h-16 text-slate-600 mb-3" />
              <p className="text-sm text-slate-400 mb-1">Không thể preview file này</p>
              <p className="text-[11px] text-slate-600">Vui lòng tải về để mở</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-3 border-t border-[#334155]">
          <a href={proxyUrl} download={fileName} target="_blank" rel="noopener noreferrer" className="inline-flex" data-testid="file-viewer-download">
            <Button variant="outline" className="border-[#334155] text-slate-300 hover:bg-slate-700">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Tải về
            </Button>
          </a>
          <a href={discordUrl} target="_blank" rel="noopener noreferrer" className="inline-flex">
            <Button variant="outline" className="border-[#334155] text-slate-300 hover:bg-slate-700">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Mở Discord CDN
            </Button>
          </a>
          {attachment.accounting_import_status === "success" && (
            <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 ml-auto">
              Đã import → {attachment.accounting_target_module}
            </Badge>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
